import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { Button } from "../components/ui/button";
import { NAV_LINKS } from "../data/content";
import DemoDialog from "./DemoDialog";

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [demoOpen, setDemoOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-navbar"
        className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
          scrolled
            ? "bg-slate-950/85 backdrop-blur-2xl border-b border-white/5"
            : "bg-transparent"
        }`}
      >
        <div className="max-w-7xl mx-auto w-full flex items-center justify-between px-6 md:px-10 h-16 md:h-20">
          <Link
            to="/"
            data-testid="navbar-logo"
            className="flex items-center gap-2.5 group flex-shrink-0"
          >
            <img
              src="/gen-x.png"
              alt="GenX Technologies logo"
              className="h-8 w-auto md:h-10 object-contain"
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {NAV_LINKS.map((link) => (
              <NavLink
                key={link.to}
                to={link.to}
                end={link.to === "/"}
                data-testid={`nav-link-${link.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `relative px-4 py-2 text-sm transition-colors ${
                    isActive
                      ? "text-cyan-300"
                      : "text-slate-300 hover:text-cyan-300"
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    {link.label}
                    {isActive && (
                      <span className="absolute left-3 right-3 -bottom-0.5 h-px bg-cyan-400" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
          </nav>

          

          <button
            data-testid="mobile-menu-toggle"
            className="lg:hidden text-slate-100 p-2"
            onClick={() => setOpen((o) => !o)}
            aria-label="Toggle menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>

        {open && (
          <div
            data-testid="mobile-menu"
            className="lg:hidden bg-slate-950/95 backdrop-blur-xl border-t border-white/5"
          >
            <div className="px-6 py-4 flex flex-col gap-1">
              {NAV_LINKS.map((link) => (
                <NavLink
                  key={link.to}
                  to={link.to}
                  end={link.to === "/"}
                  data-testid={`mobile-nav-${link.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `block px-3 py-3 border-b border-white/5 ${
                      isActive ? "text-cyan-300" : "text-slate-200"
                    }`
                  }
                >
                  {link.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}
      </header>
      <DemoDialog open={demoOpen} onOpenChange={setDemoOpen} />
    </>
  );
}
